import Vue from "vue";
import Component from "vue-class-component";
import { Watch, Prop } from "vue-property-decorator";
import Axios from "axios";
import { easepick } from '@easepick/core';
import { RangePlugin } from '@easepick/range-plugin';
import { LockPlugin } from '@easepick/lock-plugin';
import Localizer from "@Scripts/Common/Localizer";
import GoogleMapsLocator from "@Scripts/Infrastructure/GoogleMapsLocator";

@Component({ name: "Locator" })
export default class Locator extends Vue {
    public localizer: any = Localizer;
    public googleMapsLocator = new GoogleMapsLocator();
    public datePicker: any = null;
    public isInitialLoading: boolean = true;

    public dealerPointFilter: any = {
        topRightLatitude: null,
        topRightLongitude: null,
        bottomLeftLatitude: null,
        bottomLeftLongitude: null,
        bikeModelName: null
    };

    public dealerPoints: any[] = [];
    public models: any[] = [];
    public selectedModel: any = null;
    public showModelSuggestions: boolean = false;

    public hideList: boolean = false;

    public enablePeriod: boolean = false;
    public enableModel: boolean = false;

    public mapErrors: any[] = [];
    public markers: any[] = [];
    public bounds: any = new (<any>window).google.maps.LatLngBounds();

    public showMobileMap: boolean = false;

    public searchText: string = null;
    public searchSuggestions: any[] = [];
    public disableWatch: boolean = false;
    public debounceTimeOut: any = null;

    @Prop() readonly language: string;
    @Prop() readonly recaptchaSiteKey: string;

    @Watch("searchText") onSearchTextChanged(value: any, oldValue: any) {
        if (!this.disableWatch) {
            this.debounceSearchText(value, 500);
        }
    }

    @Watch("$route") async onUrlChange(newUrl: any) {
        if (!this.$route.params.dealerPointCode) {
            if (this.dealerPoints.length === 0) {
                await this.getDealerPoints();
            }

            this.setMarkers();
            this.googleMapsLocator.closeMarkerInfoWindow();

            this.hideList = false;
        } else {
            this.hideList = true;
        }
    }

    public async created() {
        await this.getDealerPoints();

        this.googleMapsLocator.init(false);

        this.setMarkers();

        if (this.$route.params.dealerPointCode) {
            this.hideList = true;

            let dealerPoint = this.dealerPoints.find(x => x.code === this.$route.params.dealerPointCode);
            if (dealerPoint.latitude && dealerPoint.longitude) {
                let latLng = new (<any>window).google.maps.LatLng(dealerPoint.latitude, dealerPoint.longitude);
                this.googleMapsLocator.centerMap(latLng);
                this.googleMapsLocator.openMarkerInfoWindow(this.markers.find(x => x.identifier === dealerPoint.idMaster), this.getInfoWindowContent(dealerPoint));
            }
        }

        this.isInitialLoading = false;
    }

    public async getDealerPoints() {
        let data = (await Axios.post((<any>window).App.routes.YOURent_GetDealerPoints, this.dealerPointFilter)).data;
        this.dealerPoints = data.dealerPoints;
        this.models = data.models;
    }

    public setMarkers() {
        if (this.dealerPoints && this.dealerPoints.length !== 0) {
            this.markers = [];
            this.bounds = new (<any>window).google.maps.LatLngBounds();

            for (let dealerPoint of this.dealerPoints) {
                if (dealerPoint.latitude && dealerPoint.longitude) {
                    let dealerPointPosition = new (<any>window).google.maps.LatLng(dealerPoint.latitude, dealerPoint.longitude);

                    this.markers.push(this.googleMapsLocator.newMarker(dealerPointPosition, "marker.svg", dealerPoint.idMaster));

                    this.bounds.extend(new (<any>window).google.maps.LatLng(dealerPoint.latitude, dealerPoint.longitude), "marker.svg", dealerPoint.idMaster);
                }
            }
            this.googleMapsLocator.fitBounds(this.bounds);
        }
    }

    public getInfoWindowContent(dealerPoint: any) {
        let content = "<div class='google-maps-marker' id='MarkerTemplate'><address class='infowindow-contents info-dealer cf'><div id='MarkerTitle' class='infowindow-title name-zone-distance'><div class='name'><span>" + dealerPoint.company + "</span><span class='zone'>" + dealerPoint.location + " (" + dealerPoint.province + ")</div></div><div id='MarkerDescription' class='infowindow-description'>" + dealerPoint.address + " " + dealerPoint.postCode + "</div></address></div>"
        return content;
    }

    public async getSearchSuggestions(searchText: string) {
        let self = this;
        let autocompleteService = new (<any>window).google.maps.places.AutocompleteService();

        autocompleteService.getPlacePredictions({ componentRestrictions: { country: "it" }, input: searchText, types: ["geocode"] }, async function (suggestions) {
            if (suggestions) {
                let placeSuggestions = suggestions.map(x => ({
                    description: x.description,
                    address: x.description,
                    type: "Place"
                }));

                self.dealerPointFilter.searchText = searchText;

                let dealerPointsSuggestions = (await Axios.post((<any>window).App.routes.YOURent_GetDealerPointSuggestions, self.dealerPointFilter)).data;

                self.searchSuggestions = [...dealerPointsSuggestions, ...placeSuggestions];
            }
        });
    }

    public debounceSearchText(searchText: string, delay: number) {
        if (searchText && searchText.length >= 3) {
            clearTimeout(this.debounceTimeOut);
            this.debounceTimeOut = setTimeout(function (context) { context.getSearchSuggestions(searchText) }, delay, this);
        }
    }

    public async selectSuggestion(suggestion: any) {
        let self = this;

        this.mapErrors = [];

        this.googleMapsLocator.geocoder.geocode({ address: suggestion.address }, async function (data, status) {
            if (status === (<any>window).google.maps.GeocoderStatus.OK && data) {
                let place = data[0];

                if (place && !place.geometry) {
                    self.mapErrors.push(self.localizer.get("YOURent.Common.AddressNotFound"));
                    setTimeout(function () { self.mapErrors = []; }, 3000);
                    return;
                } else {
                    self.disableWatch = true;
                    self.searchText = place.formatted_address;

                    setTimeout(function () { self.disableWatch = false; }, 1000);

                    if (suggestion.Type === "DealerPoint") {
                        let dealerPoint = self.dealerPoints.find(x => x.code === suggestion.dealerPointCode);

                        self.googleMapsLocator.centerMap(place.geometry.location);
                        self.googleMapsLocator.openMarkerInfoWindow(self.markers.find(x => x.identifier === dealerPoint.idMaster), self.getInfoWindowContent(dealerPoint));

                        self.dealerPointDetail(suggestion.dealerPointCode);
                    } else {
                        let bounds = new (<any>window).google.maps.Circle({ center: place.geometry.location, radius: 45000 }).getBounds();
                        self.googleMapsLocator.centerMap(place.geometry.location);

                        self.dealerPointFilter.topRightLatitude = bounds.getNorthEast().lat();
                        self.dealerPointFilter.topRightLongitude = bounds.getNorthEast().lng();
                        self.dealerPointFilter.bottomLeftLatitude = bounds.getSouthWest().lat();
                        self.dealerPointFilter.bottomLeftLongitude = bounds.getSouthWest().lng();

                        await self.getDealerPoints();

                        self.setMarkers();

                        self.enablePeriod = true;

                        self.$nextTick(function () {
                            self.loadDatePicker();
                        });
                    }

                    self.clearSuggestions();
                }
            } else {
                self.clearLocationSearch()
            }
        });
    }

    public async clearSuggestions() {
        this.searchSuggestions = [];
    }

    public async clearLocationSearch() {
        this.searchSuggestions = [];
        this.searchText = null;

        this.dealerPointFilter.topRightLatitude = null;
        this.dealerPointFilter.topRightLongitude = null;
        this.dealerPointFilter.bottomLeftLatitude = null;
        this.dealerPointFilter.bottomLeftLongitude = null;

        this.clearDatePicker()

        await this.getDealerPoints();
        this.setMarkers();

        this.enablePeriod = false;
        this.enableModel = false;
    }

    public async selectModel(model: any) {
        this.selectedModel = model;
        this.dealerPointFilter.bikeModelName = model.name;
        await this.getDealerPoints();
        this.setMarkers();
        this.showModelSuggestions = false;
    }

    public async clearModel() {
        this.dealerPointFilter.bikeModelName = null;
        await this.getDealerPoints();
        this.setMarkers();
    }

    public toggleModelSuggestions() {
        this.showModelSuggestions = !this.showModelSuggestions;
    }

    public async loadDatePicker() {
        let lockedDays = (await Axios.post((<any>window).App.routes.YOURent_GetLockedDays, {})).data;

        const picker = new easepick.create({
            element: document.getElementById("Datepicker"),
            css: [
                'https://cdn.jsdelivr.net/npm/@easepick/core@1.2.1/dist/index.css',
                'https://cdn.jsdelivr.net/npm/@easepick/range-plugin@1.2.1/dist/index.css',
                'https://cdn.jsdelivr.net/npm/@easepick/lock-plugin@1.2.1/dist/index.css',
                '/styles/easepick.css',
            ],
            format: "DD\/MM\/YYYY",
            plugins: [LockPlugin, RangePlugin],
            LockPlugin: {
                minDate: Date.now(),
                inseparable: true,
                filter(date: any) {
                    let formatdate = date.format('YYYY-MM-DD');
                    return lockedDays.includes(formatdate);
                }
            },
            RangePlugin: {
                tooltip: false
            }
        });

        picker.on("select", async () => {
            this.dealerPointFilter.dateStart = this.getDateWithOutOffset(picker.getStartDate());
            this.dealerPointFilter.dateEnd = this.getDateWithOutOffset(picker.getEndDate());

            await this.getDealerPoints();
            this.setMarkers();
            this.enableModel = true;
        });
    }

    public getDateWithOutOffset(date: Date) {
        const offset = date.getTimezoneOffset()
        date = new Date(date.getTime() - (offset * 60 * 1000))
        return date.toISOString().split('T')[0]
    }

    public clearDatePicker() {
        if (this.datePicker) {
            this.datePicker.clearSelection();
        }
    }

    public async dealerPointDetail(dealerPointCode: string) {
        let dealerPoint = this.dealerPoints.find(x => x.code === dealerPointCode);
        if (dealerPoint.latitude && dealerPoint.longitude) {
            let latLng = new (<any>window).google.maps.LatLng(dealerPoint.latitude, dealerPoint.longitude);
            this.googleMapsLocator.centerMap(latLng);
            this.googleMapsLocator.openMarkerInfoWindow(this.markers.find(x => x.identifier === dealerPoint.idMaster), this.getInfoWindowContent(dealerPoint));
        }

        this.$router.push({ name: "DealerPointDetail", params: { dealerPointCode: dealerPointCode.toString(), dateStart: this.dealerPointFilter.dateStart, dateEnd: this.dealerPointFilter.dateEnd, modelName: this.dealerPointFilter.bikeModelName } });
    }

    public toggleMobileMap() {
        this.showMobileMap = !this.showMobileMap;
    }
}